import {
  Avatar,
  Badge,
  CollapseCard,
  FlexBox,
  Icon,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useMemo, useRef } from 'react';

import { ConversationCustomFieldsForm } from 'components/conversation/conversationCustomFieldsForm';
import { useCustomAttributeSuggestions } from 'components/conversation/conversationCustomFieldsForm/utils/useCustomAttributeSuggestions';
import {
  useCustomFields,
  useRequiredAndVisibleCustomFields,
} from 'utils/customFields';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag/useFeatureFlag';
import { UseOrderedDragDropOptions } from 'utils/useOrderedDragDrop';
import { useCurrentUserInAcw } from 'utils/user';

import { CardMoveHandle } from '../../components/cardMoveHandle/cardMoveHandle';
import { VisibilityToggle } from '../../components/visibilityToggle/visibilityToggle';

import styles from './conversationCustomFieldsCard.module.scss';

type ConversationCustomFieldsCardProps = Pick<
  UseOrderedDragDropOptions,
  'type' | 'index' | 'onMove'
> & {
  csid: number;
  isMinimized: boolean;
  isOpen: boolean;
  onToggle: () => void;
};

export const ConversationCustomFieldsCard = ({
  csid,
  isMinimized,
  isOpen,
  index,
  type,
  onMove,
  onToggle,
}: ConversationCustomFieldsCardProps): JSX.Element | null => {
  const row = useRef<HTMLDivElement>(null);
  const { customFields, rawResponse } = useCustomFields({
    entityId: 'conversation',
    entityInstanceId: `${csid}`,
  });

  const isCurrentUserInAcw = useCurrentUserInAcw({ csid });

  const missingRequiredAndVisibleAttributesCount =
    useRequiredAndVisibleCustomFields(customFields).length;

  const flags = useFeatureFlag(['custom-attributes-suggestions']);
  const {
    suggestions,
    isFetching: isFetchingSuggestions,
    isError: isErrorSuggestions,
  } = useCustomAttributeSuggestions({
    customFields,
    csid,
  });

  const requiredAttributesBadge = useMemo(() => {
    if (!missingRequiredAndVisibleAttributesCount) {
      return null;
    }

    return (
      <Tooltip
        content={
          <FlexBox flexDirection="column" gap="100">
            <Text.Paragraph as="span">
              {missingRequiredAndVisibleAttributesCount} required{' '}
              {missingRequiredAndVisibleAttributesCount > 1
                ? 'attributes (*) are'
                : 'attribute (*) is'}{' '}
              missing value.
            </Text.Paragraph>

            {isCurrentUserInAcw && (
              <Text.Paragraph as="span">
                Wrap-up time will not end until all required attributes are
                filled in.
              </Text.Paragraph>
            )}
          </FlexBox>
        }
        wrapProps={{ className: styles.tooltipWrap }}
      >
        <Badge variant="warning" size="small">
          {missingRequiredAndVisibleAttributesCount} *
        </Badge>
      </Tooltip>
    );
  }, [isCurrentUserInAcw, missingRequiredAndVisibleAttributesCount]);

  const suggestionsBadge = useMemo(() => {
    if (!flags.customAttributesSuggestions) {
      return null;
    }

    const content = isErrorSuggestions
      ? 'Failed to load AI suggestions'
      : isFetchingSuggestions
        ? 'Loading AI suggestions...'
        : Object.keys(suggestions).length
          ? 'AI suggestions available'
          : 'No AI suggestions found';

    return (
      <Tooltip content={content} wrapProps={{ className: styles.tooltipWrap }}>
        <Badge variant="primary" size="small">
          <Icon name="ai" size={12} />
        </Badge>
      </Tooltip>
    );
  }, [
    flags.customAttributesSuggestions,
    isFetchingSuggestions,
    isErrorSuggestions,
    suggestions,
  ]);

  const inputIcon = <Avatar icon="input" />;

  if (!customFields.length || !rawResponse.attributes?.isSuccess) {
    return null;
  }

  if (isMinimized) {
    return (
      <FlexBox flexDirection="column" gap="100">
        <div className="icon-holder">{inputIcon}</div>
        {requiredAttributesBadge}
      </FlexBox>
    );
  }

  return (
    <CollapseCard containerRef={row} isOpen={isOpen} onToggle={onToggle}>
      <CollapseCard.Header
        avatar={inputIcon}
        slotBeforeTitle={
          <CardMoveHandle
            index={index}
            target={row}
            type={type}
            onMove={onMove}
          />
        }
        slotAfterTitle={
          <FlexBox alignItems="center" gap="100">
            {requiredAttributesBadge}
            {suggestionsBadge}
            <VisibilityToggle />
          </FlexBox>
        }
      >
        Conversation attributes
      </CollapseCard.Header>

      <CollapseCard.Content>
        <ConversationCustomFieldsForm
          customFields={customFields}
          csid={csid}
          suggestions={suggestions}
        />
      </CollapseCard.Content>
    </CollapseCard>
  );
};
